import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useRole,
  useTransitionStyles
} from "@floating-ui/react";
import { filterClasses } from "@swivl-mono/helpers";
import { FeatureFlagKey, MyRoutingGroup } from "@swivl-mono/types";
import { Tooltip } from "@swivl-mono/ui";
import React, { useEffect, useState } from "react";
import { FaCaretDown } from "react-icons/fa";
import { FaTicket, FaUserGroup } from "react-icons/fa6";
import { HiOutlineSearch } from "react-icons/hi";
import { HiMiniInbox } from "react-icons/hi2";
import { RiEditBoxLine } from "react-icons/ri";
import { TbLayoutSidebarFilled } from "react-icons/tb";

import { isFeatureEnabled } from "../../../Helpers/FeatureFlags/isFeatureEnabled";
import { useTicketingStore } from "../../../Models/Ticketing/Ticketing.Store";

export type InboxViewMode = "my" | "all" | "group" | "search";

interface TicketInboxNavProps {
  viewMode: InboxViewMode;
  activeGroupSlug: string | null;
  onSelectView: (mode: "my" | "all") => void;
  onSelectGroup: (slug: string) => void;
  /** Select the aggregate Group Inbox (all my groups, no single-group filter). */
  onSelectGroupInbox: () => void;
  onSearchAll: () => void;
  onCreate: () => void;
  /** Drop the right divider when the nav sits in its own card (e.g. Search All). */
  hideBorder?: boolean;
}

/**
 * Left navigation rail for the Tickets inbox: Search All, My Inbox,
 * Group Inbox (expands to the member's routing groups with unaccepted-count
 * badges), and All Tickets.
 *
 * The rail collapses to a 48px icon-only strip via the sidebar toggle in the
 * header; the sibling list panel (flex-1) widens to fill the reclaimed space.
 *
 * Open/collapse animates: a single persistent container transitions its width
 * (180px ⇆ 48px). The inner content keeps its natural width and is clipped by
 * the container's `overflow-hidden`, so labels reveal/clip smoothly rather than
 * popping. Respects `prefers-reduced-motion`.
 */
export function TicketInboxNav({
  viewMode,
  activeGroupSlug,
  onSelectView,
  onSelectGroup,
  onSelectGroupInbox,
  onSearchAll,
  onCreate,
  hideBorder
}: TicketInboxNavProps): React.JSX.Element {
  const myGroups = useTicketingStore((s) => s.myGroups);
  const loadMyGroups = useTicketingStore((s) => s.loadMyGroups);
  const [groupsExpanded, setGroupsExpanded] = useState(true);
  const [collapsed, setCollapsed] = useState(false);
  // Hide all Group surfaces when the org doesn't have the ticketing-groups flag.
  const groupsEnabled = isFeatureEnabled(FeatureFlagKey.TicketingGroups);

  useEffect(() => {
    void loadMyGroups();
  }, [loadMyGroups]);

  const totalUnaccepted = myGroups.reduce((sum, g) => sum + g.unacceptedCount, 0);
  // The aggregate Group Inbox is selected when we're in group mode with no
  // single group active.
  const groupInboxActive = viewMode === "group" && activeGroupSlug === null;

  return (
    <div
      className={filterClasses(
        "flex h-full shrink-0 flex-col overflow-hidden transition-[width] duration-200 ease-in-out motion-reduce:transition-none",
        !hideBorder && "border-r border-grey-200",
        collapsed ? "w-12" : "w-[180px]"
      )}
    >
      {collapsed ? (
        <div className="flex min-h-0 w-12 flex-1 flex-col items-center">
          {/* Header — only the toggle, centered */}
          <div className="flex h-12 w-full shrink-0 items-center justify-center border-b border-grey-200">
            <Tooltip content="Open Sidebar" placement="right" tooltipOffset={4}>
              <button
                type="button"
                onClick={() => setCollapsed(false)}
                className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-black transition hover:bg-grey-50"
                aria-label="Open Sidebar"
              >
                <TbLayoutSidebarFilled className="h-4 w-4" />
              </button>
            </Tooltip>
          </div>

          {/* Items — icon-only, labels hidden; titles provide hover tooltips */}
          <div className="flex min-h-0 flex-1 flex-col items-center gap-1 overflow-auto py-2">
            <RailIcon icon={<RiEditBoxLine className="h-4 w-4" />} title="Create Ticket" onClick={onCreate} />
            <RailIcon icon={<HiOutlineSearch className="h-4 w-4" />} title="Search All" active={viewMode === "search"} onClick={onSearchAll} />
            <RailIcon
              icon={<HiMiniInbox className="h-4 w-4" />}
              title="My Inbox"
              active={viewMode === "my"}
              badge={totalUnaccepted}
              onClick={() => onSelectView("my")}
            />
            {groupsEnabled && myGroups.length > 0 && (
              <CollapsedGroupNav
                groups={myGroups}
                viewMode={viewMode}
                activeGroupSlug={activeGroupSlug}
                totalUnaccepted={totalUnaccepted}
                onSelectGroupInbox={onSelectGroupInbox}
                onSelectGroup={onSelectGroup}
              />
            )}
            <RailIcon icon={<FaTicket className="h-4 w-4" />} title="All Tickets" active={viewMode === "all"} onClick={() => onSelectView("all")} />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 w-[180px] flex-1 flex-col">
          {/* Header — pl-2.5 (not px-4) so the toggle glyph, centered in its 28px
              hover box, lines up at 16px with the nav icons below. */}
          <div className="flex h-12 shrink-0 items-center justify-between border-b border-grey-200 pr-4 pl-2.5">
            <div className="flex items-center gap-1">
              <Tooltip content="Collapse Sidebar" placement="right" tooltipOffset={4}>
                <button
                  type="button"
                  onClick={() => setCollapsed(true)}
                  className="flex h-7 w-7 cursor-pointer items-center justify-center rounded text-black transition hover:bg-grey-50"
                  aria-label="Collapse Sidebar"
                >
                  <TbLayoutSidebarFilled className="h-4 w-4" />
                </button>
              </Tooltip>
              <span className="heading-base-alt text-black">Inbox</span>
            </div>
            <button
              onClick={onCreate}
              className="flex h-4 w-4 cursor-pointer items-center justify-center text-grey-900 transition hover:text-orange-500"
              type="button"
              title="Create ticket"
            >
              <RiEditBoxLine className="h-4 w-4" />
            </button>
          </div>

          {/* Items */}
          <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-auto px-1.5 py-2">
            <NavItem icon={<HiOutlineSearch className="h-4 w-4" />} label="Search All" active={viewMode === "search"} onClick={onSearchAll} />
            <NavItem
              icon={<HiMiniInbox className="h-4 w-4" />}
              label="My Inbox"
              active={viewMode === "my"}
              badge={totalUnaccepted}
              onClick={() => onSelectView("my")}
            />

            {groupsEnabled && myGroups.length > 0 && (
              <>
                {/* Group Inbox — clicking anywhere on the row (including the empty
                space) opens the aggregate view (all my groups); the caret
                expands/collapses the single group lanes below (down = expanded,
                up = collapsed) without selecting. */}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={onSelectGroupInbox}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectGroupInbox();
                    }
                  }}
                  aria-label="Group Inbox"
                  className={filterClasses(
                    "flex h-[38px] cursor-pointer items-center rounded transition",
                    groupInboxActive ? "bg-grey-50" : "hover:bg-grey-50"
                  )}
                >
                  <div className="flex min-w-0 items-center gap-2 pl-2.5">
                    <FaUserGroup className={filterClasses("h-4 w-4 shrink-0", groupInboxActive ? "text-grey-900" : "text-grey-700")} />
                    <span className={filterClasses(groupInboxActive ? "body-sm-alt text-black" : "body-sm text-grey-700", "truncate")}>
                      Group Inbox
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setGroupsExpanded((v) => !v);
                    }}
                    aria-expanded={groupsExpanded}
                    aria-label={groupsExpanded ? "Collapse groups" : "Expand groups"}
                    className="flex h-full shrink-0 cursor-pointer items-center rounded pr-2.5 pl-1"
                  >
                    <FaCaretDown
                      className={filterClasses(
                        "h-3 w-3 transition-transform motion-reduce:transition-none",
                        groupInboxActive ? "text-grey-900" : "text-grey-700",
                        groupsExpanded ? "" : "rotate-180"
                      )}
                    />
                  </button>
                </div>

                {/* Group lanes — stay mounted so the show/hide can animate. The
                grid-rows 0fr⇆1fr trick slides them open/closed to their natural
                height; the inner overflow-hidden clips content mid-slide. The
                negative margin when collapsed cancels the parent's gap so the
                closed state doesn't leave an extra 4px gap. */}
                <div
                  className={filterClasses(
                    "grid transition-[grid-template-rows,margin] duration-200 ease-in-out motion-reduce:transition-none",
                    groupsExpanded ? "grid-rows-[1fr]" : "-mb-1 grid-rows-[0fr]"
                  )}
                >
                  <div className="flex min-h-0 flex-col gap-1 overflow-hidden">
                    {myGroups.map((g) => (
                      <button
                        key={g.slug}
                        type="button"
                        onClick={() => onSelectGroup(g.slug)}
                        className={filterClasses(
                          "flex h-[38px] cursor-pointer items-center gap-2 rounded py-2.5 pr-2.5 pl-[34px] text-left transition",
                          viewMode === "group" && activeGroupSlug === g.slug ? "bg-grey-50" : "hover:bg-grey-50"
                        )}
                      >
                        <span
                          className={filterClasses(
                            viewMode === "group" && activeGroupSlug === g.slug ? "body-sm-alt text-black" : "body-sm text-grey-700",
                            "flex-1 truncate"
                          )}
                        >
                          {g.name}
                        </span>
                        {g.unacceptedCount > 0 && (
                          <span className="body-mono-alt flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-center text-white">
                            {g.unacceptedCount}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <NavItem icon={<FaTicket className="h-4 w-4" />} label="All Tickets" active={viewMode === "all"} onClick={() => onSelectView("all")} />
          </div>
        </div>
      )}
    </div>
  );
}

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  /** Unaccepted-count badge; rendered as a red pill when > 0. */
  badge?: number;
  onClick: () => void;
}

function NavItem({ icon, label, active, badge, onClick }: NavItemProps): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={filterClasses(
        "flex h-[38px] cursor-pointer items-center gap-2 rounded px-2.5 text-left transition",
        active ? "bg-grey-50" : "hover:bg-grey-50"
      )}
    >
      <span className={filterClasses("shrink-0", active ? "text-grey-900" : "text-grey-700")}>{icon}</span>
      <span className={filterClasses(active ? "body-sm-alt text-black" : "body-sm text-grey-700", "flex-1 truncate")}>{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className="body-mono-alt flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-center text-white">
          {badge}
        </span>
      )}
    </button>
  );
}

interface RailIconProps {
  icon: React.ReactNode;
  /** Hover tooltip — stands in for the hidden label in the collapsed rail. */
  title: string;
  active?: boolean;
  /** Unaccepted-count badge; rendered as a small dot when > 0. */
  badge?: number;
  onClick: () => void;
}

function RailIcon({ icon, title, active, badge, onClick }: RailIconProps): React.JSX.Element {
  return (
    <Tooltip content={title} placement="right" tooltipOffset={4}>
      <button
        type="button"
        onClick={onClick}
        aria-label={title}
        className={filterClasses(
          "relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded transition",
          active ? "bg-grey-50 text-grey-900" : "text-grey-700 hover:bg-grey-50"
        )}
      >
        {icon}
        {badge !== undefined && badge > 0 && (
          <span className="body-mono-alt absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-center text-white">
            {badge}
          </span>
        )}
      </button>
    </Tooltip>
  );
}

interface CollapsedGroupNavProps {
  groups: MyRoutingGroup[];
  viewMode: InboxViewMode;
  activeGroupSlug: string | null;
  totalUnaccepted: number;
  /** Clicking the icon opens the aggregate Group Inbox (all my groups). */
  onSelectGroupInbox: () => void;
  onSelectGroup: (slug: string) => void;
}

/**
 * Collapsed-rail Group Inbox icon. Clicking opens the aggregate Group Inbox view
 * (all my groups); hovering opens a floating card (portaled, so it escapes the
 * rail's overflow-clip) listing the member's groups, each jumping to that
 * group's lane. `safePolygon` keeps the card open while the cursor travels from
 * the icon onto it.
 */
function CollapsedGroupNav({
  groups,
  viewMode,
  activeGroupSlug,
  totalUnaccepted,
  onSelectGroupInbox,
  onSelectGroup
}: CollapsedGroupNavProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const { context, refs, floatingStyles } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: "right-start",
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip(), shift({ padding: 8 })]
  });
  const hover = useHover(context, { handleClose: safePolygon(), delay: { open: 0, close: 80 } });
  const role = useRole(context, { role: "menu" });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([hover, role, dismiss]);
  const { isMounted, styles } = useTransitionStyles(context, { duration: { open: 120, close: 100 } });

  const active = viewMode === "group";

  return (
    <>
      <button
        ref={refs.setReference}
        {...getReferenceProps()}
        type="button"
        onClick={onSelectGroupInbox}
        aria-label="Group Inbox"
        className={filterClasses(
          "relative flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded text-grey-900 transition",
          active ? "bg-grey-50" : "hover:bg-grey-50"
        )}
      >
        <FaUserGroup className="h-4 w-4" />
        {totalUnaccepted > 0 && (
          <span className="body-mono-alt absolute top-1 right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-center text-white">
            {totalUnaccepted}
          </span>
        )}
      </button>

      {isMounted && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, ...styles }}
            {...getFloatingProps()}
            className="z-600 min-w-[140px] rounded-lg border border-grey-200 bg-white p-1.5 shadow-[0px_1px_4px_0px_rgba(0,0,0,0.15)]"
          >
            {groups.map((g) => {
              const isActive = viewMode === "group" && activeGroupSlug === g.slug;
              return (
                <button
                  key={g.slug}
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    onSelectGroup(g.slug);
                  }}
                  className={filterClasses(
                    "flex h-[38px] w-full cursor-pointer items-center gap-2 rounded px-2.5 text-left transition",
                    isActive ? "bg-grey-50" : "hover:bg-grey-50"
                  )}
                >
                  <span className={filterClasses(isActive ? "body-sm-alt text-black" : "body-sm text-grey-700", "flex-1 truncate")}>{g.name}</span>
                  {g.unacceptedCount > 0 && (
                    <span className="body-mono-alt flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-center text-white">
                      {g.unacceptedCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
